import { useState } from 'react';

export interface ClientPagination<T> {
  /** The rows of the page on screen, after any kept row from another page. */
  pageItems: T[];
  /** 1-based, always within 1…pageCount. */
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  setPage: (page: number) => void;
}

export interface ClientPaginationOptions<T> {
  /** A new search or filter: the page goes back to 1 whenever this changes. */
  resetKey?: string;
  /**
   * Rows shown whichever page is on screen — a row being edited, so its save
   * and cancel stay in reach when a search or the pager moves away from it.
   */
  keep?: (item: T) => boolean;
}

/**
 * Pages a list held in the browser. The page goes back to 1 whenever
 * `resetKey` changes, and is held within the pages there are when the list
 * shrinks under it.
 */
export function useClientPagination<T>(
  items: readonly T[],
  pageSize: number,
  { resetKey = '', keep }: ClientPaginationOptions<T> = {},
): ClientPagination<T> {
  const [state, setState] = useState({ key: resetKey, page: 1 });
  // A new key starts again at page 1, and forgets the old page for good, so
  // going back to an earlier search does not bring back the page it was on.
  if (state.key !== resetKey) setState({ key: resetKey, page: 1 });
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const wanted = state.key === resetKey ? state.page : 1;
  const page = Math.min(Math.max(1, wanted), pageCount);
  const slice = items.slice((page - 1) * pageSize, page * pageSize);
  const kept = keep ? items.filter((i) => keep(i) && !slice.includes(i)) : [];
  return {
    pageItems: [...kept, ...slice],
    page,
    pageCount,
    pageSize,
    total: items.length,
    setPage: (next) => setState({ key: resetKey, page: next }),
  };
}
